import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {UrlService} from "app/core/services/url.service";
import {ErrorHandlingService} from "app/core/services/error.handling.service";
import {BehaviorSubject, Observable, tap} from "rxjs";
import {UrlEnum} from "app/core/types/url-enum";
import {ObservableErrHandler} from "app/core/types/err.handling.types";
import {InvoiceDataPerson} from "../billing.info.types";


@Injectable({providedIn: 'root'})
export class PersonInvoiceDataService
{
    private readonly _urlService: UrlService = inject(UrlService);
    private readonly _errHandlService: ErrorHandlingService = inject(ErrorHandlingService);
    private readonly _httpClient: HttpClient = inject(HttpClient);
    private readonly _data: BehaviorSubject<InvoiceDataPerson[]> = new BehaviorSubject([]);
    private readonly _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);

    get busy$() {
        return this._busy.asObservable();
    }

    /**
     * Constructor
     */
    constructor() {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */
    get data$(): Observable<InvoiceDataPerson[]> {
        return this._data.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get data
     */
    getDataFromBE(): void
    {
        if(this._data.value && this._data.value.length > 0){
            //do not get new data if data exists
            return;
        }

        this._busy.next(true);
        const obs = this._httpClient.get<InvoiceDataPerson>(
            this._urlService.getUrl(UrlEnum.private_person_billing_info));

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: value => {
                    // anounce new data
                    this._data.next(value);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    storePersonInvoiceData(dataToStore:InvoiceDataPerson):void{
        this._busy.next(true);
        if(!dataToStore || dataToStore.id){
            return;
        }

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.post<string>(
            this._urlService.getUrl(UrlEnum.private_person_billing_info), JSON.stringify(dataToStore), {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_msg: "Invoice billing info stored.",
            success_title: "Success"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: id => {
                    const newInfoArray: InvoiceDataPerson[] = this._data.value.filter(info => info.id && info.id != "");
                    const stored:InvoiceDataPerson = InvoiceDataPerson.from(dataToStore)
                    stored.id = id;
                    newInfoArray.push(stored);
                    this._data.next(newInfoArray);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    deletePersonInvoiceData(id:string):void{
        this._busy.next(true);

        const obs = this._httpClient.delete<any>(
            this._urlService.getUrl(UrlEnum.private_person_billing_info_edit).replace("{id}", id));

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_msg: "Invoice billing info deleted successfully.",
            success_title: "Success"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: value => {
                    const index = this._data.value.findIndex(info => info.id === id);
                    this._data.value.splice(index, 1);
                    this._data.next(this._data.value);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    updatePersonInvoiceData(dataToUpdate:InvoiceDataPerson):void{
        this._busy.next(true);
        if(!dataToUpdate || !dataToUpdate.id || dataToUpdate.id == ""){
            return;
        }

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.put<string>(
            this._urlService.getUrl(UrlEnum.private_person_billing_info_edit).replace("{id}",dataToUpdate.id), JSON.stringify(dataToUpdate), {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_msg: "Invoice billing info stored.",
            success_title: "Success"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: id => {
                    const newInfoArray: InvoiceDataPerson[] = this._data.value.filter(info => info.id != dataToUpdate.id);
                    newInfoArray.push(dataToUpdate);
                    this._data.next(newInfoArray);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

}
